import {
  Input,
  Component,
  forwardRef,
  ChangeDetectionStrategy,
}
  from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';


/**
 * enums
 */
import { FormFieldUI } from '@shared/modules/forms/enums/form-field-ui';

/**
 * interfaces
 */
import { ISelectItem } from './interfaces/select-item';

/**
 * utils
 */
import { FromFieldUtils } from '@sharedModules/forms/utils/form-field-utils';

/**
 * models
 */
import { SelectItemModel } from './models/select-item.model';
import { FormFieldHighOrderDirectiveBase } from '@sharedModules/forms/high-orders/form-field-high-order-directive';

/**
 * directives
 */
import { FormFieldStatusDirective } from '@sharedModules/forms/directives/form-field-status/form-field-status.directive';
import { FormFieldFeedbackDirective } from '@sharedModules/forms/directives/form-field-feedback/form-field-feedback.directive';

@Component({
  exportAs: 'selectApi',
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true
    }
  ],
  imports: [
    NgClass,
    FormsModule,
    NgTemplateOutlet,
    /**
     * directives
     */
    FormFieldStatusDirective,
    FormFieldFeedbackDirective
  ],
})
export class SelectComponent extends FormFieldHighOrderDirectiveBase<string | number | ISelectItem<any> | (string | number | ISelectItem<any>)[]> {

  /**
   * 
   */
  @Input({ alias: 'value' })
  public value: string | number | ISelectItem<any> | (string | number | ISelectItem<any>)[] = '';

  /**
   * 
   */
  @Input({ alias: 'items', required: true })
  public items: (ISelectItem<any> | any)[] = []

  /**
   * 
   */
  @Input({ alias: 'labelBinding' })
  public labelBinding!: string | null

  /**
   * 
   */
  @Input({ alias: 'valueBinding' })
  public valueBinding!: string | null

  /**
   * 
   */
  @Input({ alias: 'multiple' })
  public multiple?: boolean | null = null

  /**
   * 
   */
  @Input({ alias: 'ngModelOptions' })
  public ngModelOptions!: NgModel['options']

  /**
   * 
   */
  public get classStyleSize() {
    return FromFieldUtils.classStyleSize(this.size, FormFieldUI.Select)
  }

  /**
   * 
   */
  public getDisplayLabel(item: ISelectItem<any> | any): string {

    if (typeof item !== 'object') return item ?? ''

    if (this.labelBinding != null) return item[this.labelBinding]

    return item?.label

  }

  /**
   * 
   */
  public getValueBinding(item: ISelectItem<any> | SelectItemModel<any> | any): string | number {

    // init
    let currentItem: ISelectItem<any> | SelectItemModel<any> | any = item

    // return value depend on instanceof SelectItemModel
    if (item instanceof SelectItemModel) {
      currentItem = item.getValueBinding()
    }

    // return value depend on custom valueBinding
    if (this.valueBinding != null) return currentItem[this.valueBinding];

    return currentItem;

  }

  /**
   * 
   */
  public getTrackValue(item: ISelectItem<any> | any, index: number): number | string {

    if (typeof item === 'object') return item?.id;

    return index;

  }

  /**
   * 
   */
  public onSelectChange(value: any): void {

    // update inner value
    if (this.multiple) {
      // array expected
      this.value = value ?? [];
    } else {
      // primitive expected
      this.value = Array.isArray(value) ? value[0] : value;
    }

    // update control value
    if (this.control != null) {
      this.onChange(this.value);
    }

    // emit eventEmitter
    this.onChangeEvent.emit(this.value);

  }

  /**
   * Override writeValue to handle multiple select properly
   */
  public override writeValue(value: any): void {
    if (this.multiple) {
      // Ensure value is always an array for multiple select
      this.value = Array.isArray(value) ? value : (value != null ? [value] : []);
    } else {
      // For single select, ensure value is not an array
      this.value = Array.isArray(value) ? value[0] : value;
    }

    this.cdr.markForCheck();
  }

  /**
   * 
   */
  public onSelectTouched(): void {

    // update control status 
    if (this.control != null) {
      this.onTouched();
    }

  }

}